"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, CircleCheck } from "lucide-react";
import { cn } from "@/lib/utils";
const SERVICE_OPTIONS = ["Probiotic Fizz Formulation", "Adaptogen Seltzer Development", "Electrolyte Recovery Drink", "Functional Energy RTD", "Custom Carbonated Wellness", "Other Specification"];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="22895" className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-white/5 rounded-lg border border-[#2af598]/20 animate-in fade-in zoom-in duration-300">
        <div className="h-16 w-16 bg-[#2af598]/10 rounded-full flex items-center justify-center">
          <CircleCheck className="h-8 w-8 text-[#2af598]" />
        </div>
        <h3 className="text-xl font-sans font-semibold text-[#2af598]">Project Logged</h3>
        <p className="text-gray-300 max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-[#2af598] text-[#2af598] hover:bg-[#2af598] hover:text-[#0a1f1f] rounded-md font-mono text-xs uppercase tracking-wider">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="name" rules={{
        required: "Contact Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300 font-mono text-xs uppercase tracking-wider">Contact Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#2af598] rounded-md" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Business Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid business email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300 font-mono text-xs uppercase tracking-wider">Business Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@company.com" className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#2af598] rounded-md" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300 font-mono text-xs uppercase tracking-wider">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#2af598] rounded-md" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        {}
        <FormField control={control} name="property_address" rules={{
        required: "Company Location is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300 font-mono text-xs uppercase tracking-wider">Company Location</FormLabel>
              <FormControl>
                <Input placeholder="City, State / Country" className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#2af598] rounded-md" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="service_type" rules={{
        required: "Please select a formulation type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300 font-mono text-xs uppercase tracking-wider">Formulation Type</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={cn("flex h-9 w-full items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-gray-500 text-white focus:outline-none focus:ring-1 focus:ring-[#2af598] disabled:cursor-not-allowed disabled:opacity-50 appearance-none", !field.value && "text-gray-500")} {...field}>
                    <option value="" disabled className="bg-[#0a1f1f]">
                      Select a category
                    </option>
                    {SERVICE_OPTIONS.map((option, i) => <option key={i} value={option} data-index={i} className="bg-[#0a1f1f] text-white">
                        {option}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        {}
        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300 font-mono text-xs uppercase tracking-wider">
                Target Launch Date <span className="text-gray-500 font-normal">(Optional)</span>
              </FormLabel>
              <FormControl>
                <Input type="date" className="bg-white/5 border-white/10 text-white focus-visible:ring-[#2af598] rounded-md [color-scheme:dark]" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-gray-300 font-mono text-xs uppercase tracking-wider">
              Project Specifications <span className="text-gray-500 font-normal">(Optional)</span>
            </FormLabel>
            <FormControl>
              <textarea className="flex min-h-[100px] w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm shadow-xs placeholder:text-gray-500 text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#2af598] disabled:cursor-not-allowed disabled:opacity-50" placeholder="Tell us about your MOQ requirements, key ingredients, or functional goals..." {...field} />
            </FormControl>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-900/20 border border-red-500/50 rounded-md text-sm text-red-400">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-[#2af598] hover:bg-[#2af598]/90 text-[#0a1f1f] h-12 text-base font-sans font-semibold transition-all duration-300 shadow-lg shadow-[#2af598]/10 rounded-md">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Initializing Formulation...
          </> : "Initialize Formulation"}
      </Button>
      
      <p className="text-xs text-center text-gray-500 mt-4 font-sans">
        By submitting, you agree to our technical data protocols. We maintain strict confidentiality for all custom formulations.
      </p>
    </Form>;
}
export default function SecureYourConsultation() {
  return <section id="start-your-formulation" className="py-16 md:py-24 bg-[#0a1f1f] relative overflow-hidden dark">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#2af598]/30 to-transparent" />
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#2af598]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-[#2af598]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10 space-y-4">
            <h2 className="text-3xl md:text-5xl font-sans font-bold text-white tracking-tighter">
              Start Your Formulation
            </h2>
            <p className="text-lg text-gray-400 font-sans max-w-lg mx-auto">
              Ready to scale your beverage brand? Submit your technical requirements and our formulation experts will review your project within 24 hours.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-md p-6 md:p-8 rounded-xl border border-white/10 shadow-2xl">
            <FormProvider formKey="start_formulation_home" formId="start_formulation_home" sectionName="start-your-formulation" defaultValues={{
            name: "",
            email: "",
            phone: "",
            property_address: "",
            service_type: "",
            preferred_date: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            property_address: {
              required: true
            },
            service_type: {
              required: true
            }
          }} submitText="Initialize Formulation" successMessage="Technical specifications received. A formulation specialist from Custom RTD Co. will contact you shortly to discuss phase one.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}